import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings as SettingsIcon, Database, Users as UsersIcon, Activity, Zap, Globe, AlertCircle, ShoppingCart, Plane, TrendingUp, Bell, BellOff, Loader2, CheckCircle, Smartphone, RefreshCw, UserCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { User, UserPreferences } from '@/api/entities';
import { 
  isPushSupported, 
  getNotificationPermission, 
  setupPushNotifications, 
  teardownPushNotifications,
  getCurrentSubscription
} from '@/utils/pushNotifications';
import { toast } from 'sonner';

export default function Settings() {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchEngines, setSearchEngines] = useState({
    duffel: true,
    googleFlights: true
  });
  const [systemMetrics, setSystemMetrics] = useState({
    totalUsers: 0,
    totalBookings: 0,
    totalCarts: 0,
    activeBookings: 0,
    recentBookings: 0,
    conversionRate: 0
  });
  const [pushStatus, setPushStatus] = useState({
    supported: false,
    permission: 'default',
    subscribed: false,
    loading: false
  });
  const [migrationStatus, setMigrationStatus] = useState({
    loading: false,
    lastResult: null
  });
  const [installmentsCheckoutProvider, setInstallmentsCheckoutProvider] = useState('slice_pay');
  const [installmentsProviderLoading, setInstallmentsProviderLoading] = useState(false);
  const [deferaCapitalProcessor, setDeferaCapitalProcessor] = useState('stripe');
  const [deferaCapitalProcessorLoading, setDeferaCapitalProcessorLoading] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        const user = await User.me();
        setCurrentUser(user);

        // Load search engine preferences from Supabase
        const preferences = await UserPreferences.get();
        setSearchEngines(preferences.searchEnginePreferences);

        // Do not await: if /api is down or the proxy hangs, blocking here leaves the page on "Loading…" forever.
        fetch('/api/installments-checkout-provider')
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => {
            if (j && (j.provider === 'defera_capital' || j.provider === 'slice_pay')) {
              setInstallmentsCheckoutProvider(j.provider);
            }
          })
          .catch(() => {});

        fetch('/api/defera-capital-processor')
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => {
            if (j && (j.processor === 'stripe' || j.processor === 'nmi')) {
              setDeferaCapitalProcessor(j.processor);
            }
          })
          .catch(() => {});

        // Admin-only: do not await — a hanging /api/admin/settings-metrics or push
        // subscription check would block finally and leave the page on "Loading…" forever.
        if (user?.email === 'jayjay.gonzalez@icloud.com') {
          void loadSystemMetrics();
          void checkPushStatus();
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const checkPushStatus = async () => {
    const supported = isPushSupported();
    const permission = getNotificationPermission();
    let subscribed = false;

    try {
      if (supported && permission === 'granted') {
        const subscription = await getCurrentSubscription();
        subscribed = !!subscription;
      }
    } catch (e) {
      console.warn('Push subscription check failed:', e);
    }

    setPushStatus({
      supported,
      permission,
      subscribed,
      loading: false
    });
  };

  const handleEnablePush = async () => {
    setPushStatus(prev => ({ ...prev, loading: true }));
    try {
      await setupPushNotifications(currentUser.email);
      setPushStatus(prev => ({ 
        ...prev, 
        subscribed: true, 
        permission: 'granted',
        loading: false 
      }));
      toast.success('Push notifications enabled! You\'ll get an alert for every new request and new cart.');
    } catch (error) {
      console.error('Failed to enable push:', error);
      setPushStatus(prev => ({ ...prev, loading: false }));
      if (error.message.includes('permission denied')) {
        toast.error('Notification permission denied. Please enable in browser settings.');
      } else {
        toast.error(error.message || 'Failed to enable push notifications');
      }
    }
  };

  const handleDisablePush = async () => {
    setPushStatus(prev => ({ ...prev, loading: true }));
    try {
      await teardownPushNotifications();
      setPushStatus(prev => ({ 
        ...prev, 
        subscribed: false,
        loading: false 
      }));
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Failed to disable push:', error);
      setPushStatus(prev => ({ ...prev, loading: false }));
      toast.error('Failed to disable push notifications');
    }
  };

  const handleTestPush = async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/push/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Test notification sent to ${result.sent} device(s)`);
      } else {
        toast.error(result.error || 'Failed to send test notification');
      }
    } catch (error) {
      console.error('Test push failed:', error);
      toast.error('Failed to send test notification');
    }
  };

  const handleSimulateCart = async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/push/simulate-cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Cart notification sent! (${result.sent} device(s))`);
      } else {
        toast.error(result.error || 'Failed to simulate cart notification');
      }
    } catch (error) {
      console.error('Simulate cart failed:', error);
      toast.error('Failed to simulate cart notification');
    }
  };

  const loadSystemMetrics = async () => {
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;

      const response = await fetch('/api/admin/settings-metrics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${response.status}`);
      }

      const m = await response.json();
      setSystemMetrics({
        totalUsers: m.totalUsers ?? 0,
        totalBookings: m.totalBookings ?? 0,
        totalCarts: m.totalCarts ?? 0,
        activeBookings: m.activeBookings ?? 0,
        recentBookings: m.recentBookings ?? 0,
        conversionRate: m.conversionRate ?? 0
      });
    } catch (error) {
      console.error('Error loading system metrics:', error);
    }
  };

  const handleInstallmentsProviderChange = async (next) => {
    if (next !== 'slice_pay' && next !== 'defera_capital') return;
    setInstallmentsProviderLoading(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/admin/installments-checkout-provider', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {})
        },
        body: JSON.stringify({ provider: next })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Failed to save');
      setInstallmentsCheckoutProvider(next);
      toast.success(next === 'defera_capital' ? 'Installments now use Defera Capital (Stripe).' : 'Installments now use Slice Pay.');
    } catch (e) {
      toast.error(e.message || 'Could not update payment routing');
    } finally {
      setInstallmentsProviderLoading(false);
    }
  };

  const handleDeferaCapitalProcessorChange = async (next) => {
    if (next !== 'stripe' && next !== 'nmi') return;
    setDeferaCapitalProcessorLoading(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/admin/defera-capital-processor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {})
        },
        body: JSON.stringify({ processor: next })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Failed to save');
      setDeferaCapitalProcessor(next);
      toast.success(next === 'nmi'
        ? 'Defera Capital now routes through NMI / Vector Payments.'
        : 'Defera Capital now routes through Stripe.');
    } catch (e) {
      toast.error(e.message || 'Could not update Defera Capital processor');
    } finally {
      setDeferaCapitalProcessorLoading(false);
    }
  };

  const handleSearchEngineToggle = async (engine) => {
    const newPreferences = {
      ...searchEngines,
      [engine]: !searchEngines[engine]
    };
    setSearchEngines(newPreferences);
    
    try {
      await UserPreferences.update(newPreferences);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      // Revert on error
      setSearchEngines(searchEngines);
    }
  };

  // Data Migration: Sync missing passenger fields from cart data
  const handleSyncPassengerFields = async (dryRun = true) => {
    setMigrationStatus(prev => ({ ...prev, loading: true }));
    try {
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch('/api/admin/sync-passenger-fields', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({ dryRun })
      });

      const result = await response.json();
      
      if (result.success) {
        setMigrationStatus(prev => ({ ...prev, lastResult: result }));
        if (dryRun) {
          toast.success(`Dry run complete: ${result.results.passengersUpdated} passengers would be updated`);
        } else {
          toast.success(`Migration complete: ${result.results.passengersUpdated} passengers updated`);
        }
      } else {
        toast.error(result.error || 'Migration failed');
      }
    } catch (error) {
      console.error('Migration failed:', error);
      toast.error('Failed to run migration');
    } finally {
      setMigrationStatus(prev => ({ ...prev, loading: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <SettingsIcon className="w-8 h-8 animate-pulse text-white mx-auto mb-4" />
          <p className="text-gray-300">Loading settings...</p>
        </div>
      </div>
    );
  }

  // Only allow access for JayJay Gonzalez
  if (!currentUser || currentUser.email !== 'jayjay.gonzalez@icloud.com') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-white mb-1">Access Denied</h2>
          <p className="text-gray-300 text-sm">This page is restricted to system administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh]">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3 mb-2">
            <SettingsIcon className="w-6 h-6 text-[var(--accent-color)]" />
            System Settings
          </h2>
          <p className="text-gray-300 text-sm">Manage search engines and monitor system performance</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Push Notifications */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}>
            <Card className="glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <Bell className="w-5 h-5 text-brand-400" />
                <h2 className="text-lg font-semibold text-white">Push Notifications</h2>
              </div>
              
              <div className="space-y-4">
                {!pushStatus.supported ? (
                  <div className="p-4 glass-light rounded-lg">
                    <div className="flex items-center gap-3 text-gray-400">
                      <BellOff className="w-5 h-5" />
                      <div>
                        <p className="text-sm font-medium text-white">Not Supported</p>
                        <p className="text-xs text-gray-400">Push notifications are not supported on this device/browser</p>
                        <p className="text-xs text-gray-400 mt-1">On an iPhone: in Safari, tap Share, then Add to Home Screen, and open Defera from your Home Screen to turn them on.</p>
                      </div>
                    </div>
                  </div>
                ) : pushStatus.permission === 'denied' ? (
                  <div className="p-4 glass-light rounded-lg border border-red-500/30">
                    <div className="flex items-center gap-3 text-red-400">
                      <AlertCircle className="w-5 h-5" />
                      <div>
                        <p className="text-sm font-medium text-white">Permission Denied</p>
                        <p className="text-xs text-gray-400">Please enable notifications in your browser settings</p>
                      </div>
                    </div>
                  </div>
                ) : pushStatus.subscribed ? (
                  <div className="p-4 glass-light rounded-lg border border-green-500/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <div>
                          <p className="text-sm font-medium text-white">Notifications Enabled</p>
                          <p className="text-xs text-gray-400">You'll get an alert for every new request and new flight search</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDisablePush}
                        disabled={pushStatus.loading}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        {pushStatus.loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Disable'
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 glass-light rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Smartphone className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-white">Enable Notifications</p>
                          <p className="text-xs text-gray-400">Get an alert for every new request and new cart</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={handleEnablePush}
                        disabled={pushStatus.loading}
                        className="bg-brand-500 hover:bg-brand-600 text-black">
                        {pushStatus.loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Bell className="w-4 h-4 mr-1" />
                            Enable
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {pushStatus.subscribed && (
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestPush}
                      className="w-full border-white/20 text-white hover:bg-white/10">
                      <Bell className="w-4 h-4 mr-2" />
                      Send Test Notification
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSimulateCart}
                      className="w-full border-orange-500/30 text-orange-300 hover:bg-orange-500/10">
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Simulate New Cart Alert
                    </Button>
                  </div>
                )}
              </div>

              <div className="mt-6 p-3 bg-brand-500/10 border border-brand-500/20 rounded-lg">
                <p className="text-xs text-brand-200">
                  <Bell className="inline w-3 h-3 mr-1" />
                  Notifications include: customer name, route, and price
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Search Engine Controls */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.15 }}>
            <Card className="glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <Globe className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-white">Search Engines</h2>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 glass-light rounded-lg">
                  <div className="flex-1">
                    <Label htmlFor="googleFlights" className="text-white font-medium cursor-pointer">
                      Defera Flights
                    </Label>
                    <p className="text-xs text-gray-400 mt-1">Alternative search via SerpAPI</p>
                  </div>
                  <Switch
                    id="googleFlights"
                    checked={searchEngines.googleFlights}
                    onCheckedChange={() => handleSearchEngineToggle('googleFlights')}
                    className="data-[state=checked]:bg-green-500"
                  />
                </div>
              </div>

              <div className="mt-6 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                <p className="text-xs text-primary-foreground">
                  <Zap className="inline w-3 h-3 mr-1" />
                  Disabled engines will be hidden from the Book page
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Installments checkout: Slice Pay vs Defera Capital (Stripe) */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.18 }}>
            <Card className="glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                <h2 className="text-lg font-semibold text-white">Installments checkout</h2>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Route standard installment bookings to Slice Pay or Defera Capital (same Stripe subscription model as Defera Zero). Refundable-fare Defera Zero is unchanged.
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 glass-light rounded-lg gap-4">
                  <div className="flex-1 min-w-0">
                    <Label className="text-white font-medium">Slice Pay</Label>
                    <p className="text-xs text-gray-400 mt-1">External installment flow (current default)</p>
                  </div>
                  <Switch
                    checked={installmentsCheckoutProvider === 'slice_pay'}
                    disabled={installmentsProviderLoading}
                    onCheckedChange={(on) => on && handleInstallmentsProviderChange('slice_pay')}
                    className="data-[state=checked]:bg-slate-500 flex-shrink-0"
                  />
                </div>
                <div className="flex items-center justify-between p-4 glass-light rounded-lg gap-4">
                  <div className="flex-1 min-w-0">
                    <Label className="text-white font-medium">Defera Capital</Label>
                    <p className="text-xs text-gray-400 mt-1">Stripe subscriptions on defera.app (like Defera Zero; all eligible fares)</p>
                  </div>
                  <Switch
                    checked={installmentsCheckoutProvider === 'defera_capital'}
                    disabled={installmentsProviderLoading}
                    onCheckedChange={(on) => on && handleInstallmentsProviderChange('defera_capital')}
                    className="data-[state=checked]:bg-emerald-600 flex-shrink-0"
                  />
                </div>
              </div>
              {installmentsProviderLoading && (
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                </p>
              )}

              {installmentsCheckoutProvider === 'defera_capital' && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="mb-3">
                    <p className="text-sm font-medium text-white">Defera Capital processor</p>
                    <p className="text-xs text-gray-400 mt-1">Which payment processor runs new Defera Capital checkouts. Existing bookings keep their original processor.</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 glass-light rounded-lg gap-4">
                      <div className="flex-1 min-w-0">
                        <Label className="text-white font-medium">Stripe</Label>
                        <p className="text-xs text-gray-400 mt-1">Stripe Subscriptions (current default)</p>
                      </div>
                      <Switch
                        checked={deferaCapitalProcessor === 'stripe'}
                        disabled={deferaCapitalProcessorLoading}
                        onCheckedChange={(on) => on && handleDeferaCapitalProcessorChange('stripe')}
                        className="data-[state=checked]:bg-indigo-500 flex-shrink-0"
                      />
                    </div>
                    <div className="flex items-center justify-between p-4 glass-light rounded-lg gap-4">
                      <div className="flex-1 min-w-0">
                        <Label className="text-white font-medium">NMI / Vector Payments</Label>
                        <p className="text-xs text-gray-400 mt-1">Card-on-file vault; daily cron charges installments via merchant-initiated transactions</p>
                      </div>
                      <Switch
                        checked={deferaCapitalProcessor === 'nmi'}
                        disabled={deferaCapitalProcessorLoading}
                        onCheckedChange={(on) => on && handleDeferaCapitalProcessorChange('nmi')}
                        className="data-[state=checked]:bg-amber-500 flex-shrink-0"
                      />
                    </div>
                  </div>
                  {deferaCapitalProcessorLoading && (
                    <p className="text-xs text-gray-500 mt-3 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" /> Saving…
                    </p>
                  )}
                </div>
              )}
            </Card>
          </motion.div>

          {/* Data Migration Tools */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}>
            <Card className="glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <RefreshCw className="w-5 h-5 text-brand-400" />
                <h2 className="text-lg font-semibold text-white">Data Migration</h2>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 glass-light rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <UserCheck className="w-5 h-5 text-brand-400" />
                    <div>
                      <p className="text-sm font-medium text-white">Sync Passenger Fields</p>
                      <p className="text-xs text-gray-400">Copy missing KTN, passport, frequent flyer data from carts to bookings</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSyncPassengerFields(true)}
                      disabled={migrationStatus.loading}
                      className="flex-1 border-white/20 text-white hover:bg-white/10">
                      {migrationStatus.loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Database className="w-4 h-4 mr-1" />
                      )}
                      Dry Run
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSyncPassengerFields(false)}
                      disabled={migrationStatus.loading}
                      className="flex-1 bg-brand-500 hover:bg-brand-600 text-white">
                      {migrationStatus.loading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-1" />
                      )}
                      Run Migration
                    </Button>
                  </div>

                  {migrationStatus.lastResult && (
                    <div className="mt-3 p-3 bg-black/30 rounded-lg text-xs">
                      <p className="text-gray-300 mb-1">
                        <span className="text-white font-medium">Last run:</span> {migrationStatus.lastResult.dryRun ? 'Dry Run' : 'Actual Migration'}
                      </p>
                      <p className="text-gray-300">
                        <span className="text-green-400">{migrationStatus.lastResult.results.passengersUpdated}</span> passengers {migrationStatus.lastResult.dryRun ? 'would be' : ''} updated
                      </p>
                      <p className="text-gray-300">
                        <span className="text-gray-400">{migrationStatus.lastResult.results.passengersSkipped}</span> skipped (no changes needed)
                      </p>
                      {migrationStatus.lastResult.results.errors?.length > 0 && (
                        <p className="text-red-400">
                          {migrationStatus.lastResult.results.errors.length} errors
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 p-3 bg-brand-500/10 border border-brand-500/20 rounded-lg">
                <p className="text-xs text-brand-200">
                  <AlertCircle className="inline w-3 h-3 mr-1" />
                  Run "Dry Run" first to preview changes before applying
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Database Metrics */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25 }}>
            <Card className="glass p-6">
              <div className="flex items-center gap-3 mb-6">
                <Database className="w-5 h-5 text-green-400" />
                <h2 className="text-lg font-semibold text-white">Database Statistics</h2>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 glass-light rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <UsersIcon className="w-4 h-4 text-brand-400" />
                      <span className="text-sm text-gray-300">Total Users</span>
                    </div>
                    <span className="text-lg font-bold text-white">{systemMetrics.totalUsers}</span>
                  </div>
                </div>

                <div className="p-4 glass-light rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Plane className="w-4 h-4 text-primary" />
                      <span className="text-sm text-gray-300">Total Bookings</span>
                    </div>
                    <span className="text-lg font-bold text-white">{systemMetrics.totalBookings}</span>
                  </div>
                </div>

                <div className="p-4 glass-light rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="w-4 h-4 text-orange-400" />
                      <span className="text-sm text-gray-300">Abandoned Carts</span>
                    </div>
                    <span className="text-lg font-bold text-white">{systemMetrics.totalCarts}</span>
                  </div>
                </div>

                <div className="p-4 glass-light rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <span className="text-sm text-gray-300">Conversion Rate</span>
                    </div>
                    <span className="text-lg font-bold text-green-400">{systemMetrics.conversionRate}%</span>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <Card className="glass p-4 text-center">
            <div className="text-2xl font-bold text-white">{systemMetrics.activeBookings}</div>
            <div className="text-xs text-gray-400 mt-1">Active Bookings</div>
          </Card>
          <Card className="glass p-4 text-center">
            <div className="text-2xl font-bold text-white">{systemMetrics.recentBookings}</div>
            <div className="text-xs text-gray-400 mt-1">Bookings (24h)</div>
          </Card>
          <Card className="glass p-4 text-center">
            <div className="text-2xl font-bold text-green-400">{systemMetrics.conversionRate}%</div>
            <div className="text-xs text-gray-400 mt-1">Conversion Rate</div>
          </Card>
          <Card className="glass p-4 text-center">
            <div className="text-2xl font-bold text-primary">{Object.values(searchEngines).filter(Boolean).length}/2</div>
            <div className="text-xs text-gray-400 mt-1">Active Engines</div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
